// member-portal/src/components/MemberBilling.js — UPDATED: Show paid status + auto-scroll + latest 6 months
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import NavButtons from './NavButtons';
import './SuperAdminBillingPortal.css';

const MemberBilling = ({ navigateTo, user }) => {
  const [cycles, setCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);

  const invoicesSectionRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

  useEffect(() => {
    fetchCycles();
  }, []);

  const fetchCycles = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('memberToken');
      const res = await axios.get(`${API_URL}/api/members/billing/cycles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const latestSix = res.data.slice(0, 6);
      setCycles(latestSix);
    } catch (err) {
      console.error('Failed to load billing cycles:', err);
      setCycles([]);
    } finally {
      setLoading(false);
    }
  };

  const selectCycle = async (cycleId) => {
    setSelectedCycle(cycleId);
    setLoading(true);
    try {
      const token = localStorage.getItem('memberToken');
      const res = await axios.get(`${API_URL}/api/members/billing/cycle/${cycleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const approvedInvoices = (res.data.invoices || []).filter(inv => inv.status === 'approved');
      setInvoices(approvedInvoices);
    } catch (err) {
      console.error('Failed to load invoices for cycle:', err);
      setInvoices([]);
    } finally {
      setLoading(false);
      setTimeout(() => {
        if (invoicesSectionRef.current) {
          invoicesSectionRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
  };

  const openInvoice = (filePath) => {
    if (filePath) {
      window.open(filePath, '_blank', 'noopener,noreferrer');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-ZA');
  };

  return (
    <div className="billing-portal">
      <NavButtons navigateTo={navigateTo} user={user} activePage="billing" />
      <h1>Billing History</h1>
      <p className="subtitle">
        View, download or print your approved monthly invoices (latest 6 months).
      </p>

      {loading && cycles.length === 0 ? (
        <p>Loading billing cycles...</p>
      ) : cycles.length === 0 ? (
        <p className="no-data">No billing cycles available yet.</p>
      ) : (
        <div className="cycles-grid">
          {cycles.map(cycle => (
            <div
              key={cycle.id}
              className={`cycle-card ${selectedCycle === cycle.id ? 'selected' : ''}`}
              onClick={() => selectCycle(cycle.id)}
            >
              <h3>{cycle.period}</h3>
              <p><strong>{cycle.invoiceCount} invoice{cycle.invoiceCount !== 1 ? 's' : ''}</strong></p>
              <p className="total">R{cycle.totalAmount}</p>
            </div>
          ))}
        </div>
      )}

      <div ref={invoicesSectionRef}>
        {selectedCycle && (
          <div className="invoices-section">
            <h2>Approved Invoices</h2>
            {loading ? (
              <p>Loading invoices...</p>
            ) : invoices.length === 0 ? (
              <p className="no-data">No approved invoices for this period.</p>
            ) : (
              <table className="invoices-table">
                <thead>
                  <tr>
                    <th>Invoice Number</th>
                    <th>Period</th>
                    <th>Impressions</th>
                    <th>Views</th>
                    <th>Clicks</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(inv => (
                    <tr key={inv.id}>
                      <td>{inv.invoiceNumber}</td>
                      <td>{inv.period}</td>
                      <td>{inv.impressions?.toLocaleString() || 0}</td>
                      <td>{inv.views?.toLocaleString() || 0}</td>
                      <td>{inv.clicks?.toLocaleString() || 0}</td>
                      <td className="total">R{inv.grandTotal}</td>
                      <td>
                        {inv.paid ? (
                          <span style={{ color: 'green' }}>✓ Paid {formatDate(inv.paidAt)}</span>
                        ) : (
                          <span style={{ color: 'orange' }}>Pending Payment</span>
                        )}
                      </td>
                      <td>
                        <button
                          onClick={() => openInvoice(inv.filePath)}
                          className="review-btn"
                          disabled={!inv.filePath}
                        >
                          View / Download / Print
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberBilling;